import { Seo } from "@/components/Seo";
import { ModuleTrialPrompt } from "@/components/ModuleTrialPrompt";
import { useState, useRef, useEffect, useCallback } from "react";
import { Gavel, Loader2, Copy, Download, Sparkles, Paperclip, UploadCloud, X, Wand2 } from "lucide-react";
import api, { formatApiErrorDetail, streamPost } from "@/lib/api";
import { Disclaimer } from "@/components/Disclaimer";
import { LimitModal } from "@/components/LimitModal";
import { toast } from "@/components/ui/sonner";
import { Select, SelectContent, SelectGroup, SelectLabel, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { VoiceButton } from "@/components/VoiceButton";
import { ChatHistory } from "@/components/ChatHistory";
import { useAuth } from "@/context/AuthContext";

const DRAFT_GROUPS = [
  { group: "Reply & Custom", items: [
    { value: "Reply", label: "Reply to a Document / Application" },
    { value: "Other", label: "Other (Custom Command)" },
  ] },
  { group: "Criminal", items: ["Bail Application", "Anticipatory Bail Application", "Discharge Application", "Revision Petition", "Criminal Appeal"] },
  { group: "Civil", items: ["Plaint", "Written Statement", "Injunction Application"] },
  { group: "Constitutional", items: ["Writ Petition", "Special Leave Petition (SLP)"] },
  { group: "Corporate", items: ["Non-Disclosure Agreement (NDA)", "Shareholder Agreement", "Employment Agreement"] },
];

const ACCEPT = ".pdf,.docx,.txt,image/*,.png,.jpg,.jpeg,.webp,.heic,.heif,.tiff,.bmp";

const REFINE_CHIPS = ["Make it more formal", "Add stronger legal grounds", "Make it more concise", "Add relevant case law"];

const LANGS = [
  { code: "en", label: "English" }, { code: "hi", label: "हिन्दी · Hindi" }, { code: "gu", label: "ગુજરાતી · Gujarati" },
  { code: "mr", label: "मराठी · Marathi" }, { code: "ta", label: "தமிழ் · Tamil" }, { code: "te", label: "తెలుగు · Telugu" },
  { code: "bn", label: "বাংলা · Bengali" }, { code: "kn", label: "ಕನ್ನಡ · Kannada" }, { code: "pa", label: "ਪੰਜਾਬੀ · Punjabi" },
  { code: "ml", label: "മലയാളം · Malayalam" }, { code: "ur", label: "اردو · Urdu" },
];

export default function CounselAI() {
  const { user } = useAuth();
  const [draftType, setDraftType] = useState("Bail Application");
  const [language, setLanguage] = useState("en");
  const [details, setDetails] = useState("");
  const [file, setFile] = useState(null);
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [limitModal, setLimitModal] = useState(null);
  const [usage, setUsage] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [historyKey, setHistoryKey] = useState(0);
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [changeCmd, setChangeCmd] = useState("");
  const [showChanges, setShowChanges] = useState(false);
  const fileRef = useRef(null);

  const loadUsage = useCallback(() => {
    api.get("/counsel/usage").then(({ data }) => setUsage(data)).catch(() => {});
  }, []);
  useEffect(() => { loadUsage(); }, [loadUsage]);

  const loadDraft = async (id) => {
    try {
      const { data } = await api.get(`/counsel/drafts/${id}`);
      setDraftType(data.draft_type || "Bail Application");
      setLanguage(data.language || "en");
      setDetails(data.details || "");
      setResult(data.draft_text || "");
      setActiveDraftId(id);
    } catch {
      toast.error("Could not load that draft.");
    }
  };

  const newDraft = () => {
    setResult(""); setDetails(""); setFile(null); setActiveDraftId(null);
  };

  const showLimit = (e) => {
    const detail = e.response?.data?.detail || {};
    setLimitModal(detail.reason === "guest_limit"
      ? { mode: "guest" }
      : { mode: "free", message: detail.message });
  };

  const isReply = draftType === "Reply";
  const isOther = draftType === "Other";
  const showUpload = isReply || isOther;

  const detailsLabel = isReply ? "Reply Instructions (optional)" : isOther ? "Your Command / Details" : "Matter Details";
  const detailsPlaceholder = isReply
    ? "Who do you represent and any specific grounds? e.g. 'Represent the respondent; deny all allegations; raise the plea of limitation and lack of jurisdiction.'"
    : isOther
    ? "Tell Counsel AI exactly what to draft. e.g. 'Draft a sale deed for a flat at [address] from [seller] to [buyer] for ₹50,00,000 with possession on payment and standard indemnity clauses.'"
    : "Describe the parties, facts, court, grounds and any specifics. e.g. 'Anticipatory bail for accused in FIR No. 123/2026 u/s 420 BNS, Sessions Court Delhi…'";

  const onPickFile = (f) => {
    if (f && f.size > 25 * 1024 * 1024) { toast.error("File too large. Please upload a document under 25 MB."); return; }
    setFile(f || null);
  };

  const streamDraft = async (fields) => {
    setLoading(true);
    setResult("");
    try {
      let acc = "";
      let errored = false;
      await streamPost("/draft/stream", fields, (evt) => {
        if (evt.type === "delta") { acc += evt.text; setResult(acc); }
        else if (evt.type === "final") { setResult(evt.text); }
        else if (evt.type === "error") { errored = true; toast.error(evt.message); }
      });
      if (!errored) {
        setActiveDraftId(null);
        setHistoryKey((k) => k + 1);
        loadUsage();
      }
    } catch (e) {
      if (e.response?.status === 402) showLimit(e);
      else toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const generate = async () => {
    if (isReply && !file && !details.trim()) {
      toast.error("Upload the application to reply to, or describe it below.");
      return;
    }
    if (!isReply && !details.trim() && !file) {
      toast.error(isOther ? "Type your command, or upload a document." : "Add the matter details first.");
      return;
    }
    // Reply mode or any file upload → non-streaming multimodal path (needs the uploaded doc)
    if (file) {
      setLoading(true);
      setResult("");
      try {
        const fd = new FormData();
        fd.append("draft_type", draftType);
        fd.append("details", details);
        fd.append("language", language);
        fd.append("file", file);
        const { data } = await api.post("/draft", fd);
        setResult(data.draft);
        setActiveDraftId(null);
        setHistoryKey((k) => k + 1);
        loadUsage();
      } catch (e) {
        if (e.response?.status === 402) showLimit(e);
        else toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
      return;
    }
    // Text-only draft → live token streaming
    await streamDraft({ draft_type: draftType, details, language });
  };

  const refine = (instruction) => {
    if (!result || loading) return;
    streamDraft({ draft_type: draftType, language, base_draft: result, instruction });
  };

  const applyChanges = () => {
    const cmd = changeCmd.trim();
    if (!cmd || !result || loading) return;
    refine(cmd);
    setChangeCmd("");
    setShowChanges(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result);
      toast.success("Copied to clipboard.");
    } catch {
      toast.error("Clipboard access blocked by your browser");
    }
  };
  const download = async () => {
    setDownloading(true);
    try {
      const { data } = await api.post(
        "/draft/docx",
        { draft_text: result, title: draftType, draft_type: draftType },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${draftType.replace(/[^a-z0-9]/gi, "_")}.docx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded as an editable Word document.");
    } catch {
      toast.error("Could not export the Word file.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12" data-testid="counsel-ai-page">
      <ModuleTrialPrompt />
      <Seo title="Counsel AI — AI Legal Drafting for India | JusticeVerse" description="Draft bail applications, petitions, notices, pleadings and agreements in minutes with Counsel AI. Court-ready Indian legal documents with correct structure, clauses and statutory references." />
      <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold flex items-center gap-2">
        <Gavel className="w-4 h-4" /> Counsel AI
      </span>
      <h1 className="font-serif text-4xl sm:text-5xl font-light text-ink mt-3">VerseDraft Studio</h1>
      <p className="text-inkmuted mt-4 max-w-2xl">
        Generate professional, ready-to-edit legal drafts — applications, petitions, pleadings and agreements — in seconds.
      </p>

      <div className="flex items-center justify-between gap-3 mt-6">
        {usage ? (
          <div className="inline-flex items-center gap-2 glass-card rounded-full px-4 py-2" data-testid="counsel-usage-badge">
            <Sparkles className="w-3.5 h-3.5 text-gold" />
            {usage.unlimited
              ? <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-inkmuted">Unlimited drafts</span>
              : <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-inkmuted"><span className="text-ink">{usage.remaining}</span> of {usage.quota} drafts left{usage.signed_in ? "" : " · free"}</span>}
          </div>
        ) : <span />}
        <ChatHistory
          user={user}
          listUrl="/counsel/drafts"
          normalize={(d) => d.map((x) => ({ id: x.id, title: x.title || x.draft_type, created_at: x.created_at }))}
          onSelect={loadDraft}
          onNew={newDraft}
          deleteUrl={(id) => `/counsel/drafts/${id}`}
          deleteAllUrl="/counsel/drafts"
          activeId={activeDraftId}
          reloadKey={historyKey}
          label="Draft History"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-10">
        {/* Composer */}
        <div className="glass-card rounded-3xl p-7 space-y-5" data-testid="draft-composer">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-inkfaint">Document Type</label>
            <Select value={draftType} onValueChange={setDraftType}>
              <SelectTrigger className="mt-2 bg-surface border-hairline rounded-xl text-sm text-ink" data-testid="draft-type-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-panel border-hairline text-ink shadow-xl max-h-80">
                {DRAFT_GROUPS.map((g) => (
                  <SelectGroup key={g.group}>
                    <SelectLabel className="text-gold font-mono text-[10px] uppercase tracking-[0.2em]">{g.group}</SelectLabel>
                    {g.items.map((it) => {
                      const value = typeof it === "string" ? it : it.value;
                      const label = typeof it === "string" ? it : it.label;
                      return (
                        <SelectItem key={value} value={value} data-testid={`draft-type-${value}`} className="text-sm focus:bg-gold/15 focus:text-ink data-[state=checked]:text-gold">{label}</SelectItem>
                      );
                    })}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-inkfaint">Output Language</label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="mt-2 bg-surface border-hairline rounded-xl text-sm text-ink" data-testid="draft-lang-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-panel border-hairline text-ink shadow-xl max-h-72">
                {LANGS.map((l) => (
                  <SelectItem key={l.code} value={l.code} className="text-sm focus:bg-gold/15 focus:text-ink data-[state=checked]:text-gold">{l.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {showUpload && (
            <div data-testid="draft-upload-block">
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-inkfaint">
                {isReply ? "Upload the application / document to reply to" : "Upload a document (optional)"}
              </label>
              <div
                onClick={() => fileRef.current?.click()}
                className="mt-2 border border-dashed border-hairline rounded-xl p-5 text-center cursor-pointer hover:border-gold/40 transition-all"
                data-testid="draft-dropzone"
              >
                {file ? (
                  <div className="flex items-center justify-center gap-2 text-sm text-ink">
                    <Paperclip className="w-4 h-4 text-gold" /> {file.name}
                    <button onClick={(e) => { e.stopPropagation(); setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-inkfaint hover:text-red-400"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-1.5">
                    <UploadCloud className="w-6 h-6 text-gold" />
                    <p className="text-xs text-inkmuted">PDF, DOCX, TXT or image (scanned files read via OCR) · max 25 MB</p>
                  </div>
                )}
                <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" data-testid="draft-file-input"
                  onChange={(e) => onPickFile(e.target.files?.[0] || null)} />
              </div>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between">
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-inkfaint">{detailsLabel}</label>
              <VoiceButton lang={language} onTranscript={(t) => setDetails((p) => (p ? p + " " : "") + t)} />
            </div>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              data-testid="draft-details"
              rows={8}
              placeholder={detailsPlaceholder}
              className="mt-2 w-full bg-surface border border-hairline rounded-xl px-4 py-3 text-sm outline-none focus:border-gold/40 text-ink resize-none"
            />
            {isReply && (
              <p className="text-[11px] text-inkfaint mt-2" data-testid="reply-hint">
                Counsel AI will read the uploaded document and draft a parawise reply on your behalf.
              </p>
            )}
            {isOther && (
              <p className="text-[11px] text-inkfaint mt-2" data-testid="other-hint">
                Give any command (e.g. &ldquo;draft a sale deed with these details&rdquo;) and Counsel AI will draft it. Upload a document above to use it as the source.
              </p>
            )}
          </div>

          <button
            onClick={generate}
            disabled={loading}
            data-testid="generate-draft-btn"
            className="w-full rounded-full bg-btn text-btnink hover:bg-btnhover disabled:opacity-50 px-6 py-3.5 text-sm font-semibold uppercase tracking-wider transition-all flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {loading ? "Drafting…" : "Generate Draft"}
          </button>
        </div>

        {/* Output */}
        <div className="glass-card rounded-3xl p-7 flex flex-col" data-testid="draft-output">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-serif text-2xl text-ink">Draft Output</h3>
            {result && (
              <div className="flex items-center gap-3">
                <button onClick={copy} data-testid="copy-draft-btn" className="text-inkmuted hover:text-gold" title="Copy"><Copy className="w-4 h-4" /></button>
                <button onClick={download} disabled={downloading} data-testid="download-draft-btn" className="flex items-center gap-1.5 text-inkmuted hover:text-gold disabled:opacity-50 text-xs font-semibold uppercase tracking-wider" title="Download as editable Word (.docx)">
                  {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Word
                </button>
              </div>
            )}
          </div>
          {result && !loading && (
            <div className="mb-4" data-testid="refine-tools">
              <div className="flex flex-wrap items-center gap-2" data-testid="refine-chips">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-inkfaint mr-1">Refine</span>
                {REFINE_CHIPS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => refine(c)}
                    data-testid="refine-chip"
                    className="rounded-full border border-hairline bg-surface hover:border-gold/50 hover:text-gold text-inkmuted text-xs px-3.5 py-1.5 transition-all"
                  >
                    {c}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setShowChanges((s) => !s)}
                  data-testid="make-changes-btn"
                  className={`flex items-center gap-1.5 rounded-full border text-xs px-3.5 py-1.5 transition-all ${showChanges ? "border-gold/60 text-gold bg-gold/5" : "border-hairline bg-surface text-inkmuted hover:border-gold/50 hover:text-gold"}`}
                >
                  <Wand2 className="w-3.5 h-3.5" /> Make Changes
                </button>
              </div>
              {showChanges && (
                <div className="mt-3 rounded-2xl border border-gold/25 bg-surface p-3" data-testid="make-changes-panel">
                  <label className="block text-[11px] uppercase tracking-wider text-inkfaint mb-2">Tell Counsel AI what to change</label>
                  <textarea
                    value={changeCmd}
                    onChange={(e) => setChangeCmd(e.target.value)}
                    onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") applyChanges(); }}
                    rows={3}
                    data-testid="make-changes-input"
                    placeholder="e.g. Change the court to the High Court of Delhi, add a ground on parity, and make the prayer stronger…"
                    className="w-full bg-canvas border border-hairline rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-gold/40 text-ink resize-none"
                  />
                  <div className="flex items-center justify-end gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => { setShowChanges(false); setChangeCmd(""); }}
                      data-testid="cancel-changes-btn"
                      className="text-inkmuted hover:text-ink text-xs px-3 py-2"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={applyChanges}
                      disabled={!changeCmd.trim()}
                      data-testid="apply-changes-btn"
                      className="flex items-center gap-1.5 rounded-full bg-btn text-btnink hover:bg-btnhover disabled:opacity-50 text-xs font-semibold uppercase tracking-wider px-4 py-2 transition-all"
                    >
                      <Wand2 className="w-3.5 h-3.5" /> Apply Changes
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex-1 overflow-y-auto min-h-[40vh]">
            {loading && !result ? (
              <div className="h-full flex items-center justify-center text-inkfaint text-sm gap-2">
                <Loader2 className="w-5 h-5 animate-spin text-gold" /> Composing your {draftType.toLowerCase()}…
              </div>
            ) : result ? (
              <pre className="whitespace-pre-wrap font-sans text-sm text-ink leading-relaxed" data-testid="draft-text">{result}{loading && <span className="inline-block w-2 h-4 ml-0.5 bg-gold/70 animate-pulse align-middle" />}</pre>
            ) : (
              <div className="h-full flex items-center justify-center text-center text-inkfaint text-sm px-6">
                Your generated draft will appear here, ready to copy or download.
              </div>
            )}
          </div>
        </div>
      </div>

      <Disclaimer className="mt-6 justify-center" />
      <LimitModal mode={limitModal?.mode} freeMessage={limitModal?.message} onClose={() => setLimitModal(null)} />
    </div>
  );
}
